import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';

/**
 * Opciones de una sección o de un tipo de widget (`options_schema` del catálogo, G574). Se pintan las que se escriben o se
 * eligen de una lista (texto, número y lista cerrada). Las que señalan una zona, un servicio, un profesional o un evento
 * concretos se quedan en «La que elija el cliente»: elegir uno necesita las listas de su módulo, que aún no están en
 * esta pantalla.
 */
type Opciones = Record<string, unknown>;

interface Propiedad {
    type?: string;
    enum?: string[];
    pattern?: string;
    maxLength?: number;
    minimum?: number;
    maximum?: number;
    default?: unknown;
}

const NOMBRE: Readonly<Record<string, string>> = {
    zone_id: 'Zona',
    service_id: 'Servicio',
    professional_id: 'Profesional',
    event_id: 'Evento',
    label: 'Texto del botón',
    placement: 'Dónde va',
    hours: 'Horas que enseña',
};
const VALOR: Readonly<Record<string, string>> = { floating: 'Flotante, en una esquina', inline: 'En línea, en su hueco de la página' };

function propiedades(esquema: unknown): [string, Propiedad][] {
    const p = (esquema as { properties?: Record<string, Propiedad> } | null)?.properties;
    return p === undefined ? [] : Object.entries(p);
}

export function EditorOpciones({ esquema, valor, onChange, bloqueado = false }: { esquema: unknown; valor: Opciones; onChange: (v: Opciones) => void; bloqueado?: boolean }) {
    const cambiar = (clave: string, v: unknown) => {
        const resto = Object.fromEntries(Object.entries(valor).filter(([k]) => k !== clave));
        onChange(v === '' || v === undefined ? resto : { ...resto, [clave]: v });
    };
    return (
        <>
            {propiedades(esquema).map(([clave, p]) => {
                const nombre = NOMBRE[clave] ?? clave;
                const actual = valor[clave];
                if (p.enum !== undefined) {
                    const porDefecto = typeof p.default === 'string' ? p.default : '';
                    return (
                        <Desplegable
                            key={clave}
                            etiqueta={nombre}
                            valor={typeof actual === 'string' ? actual : porDefecto}
                            opciones={p.enum.map((e) => ({ valor: e, texto: VALOR[e] ?? e }))}
                            onChange={(v) => { cambiar(clave, v); }}
                            bloqueado={bloqueado}
                        />
                    );
                }
                if (p.pattern !== undefined) {
                    return (
                        <Desplegable
                            key={clave}
                            etiqueta={nombre}
                            valor={typeof actual === 'string' ? actual : ''}
                            opciones={[{ valor: '', texto: 'La que elija el cliente' }, ...(typeof actual === 'string' ? [{ valor: actual, texto: actual }] : [])]}
                            onChange={(v) => { cambiar(clave, v); }}
                            bloqueado={bloqueado}
                        />
                    );
                }
                if (p.type === 'integer') {
                    return (
                        <Campo
                            key={clave}
                            etiqueta={nombre}
                            tipo="number"
                            valor={typeof actual === 'number' ? String(actual) : ''}
                            onChange={(v) => { cambiar(clave, v === '' ? undefined : Number(v)); }}
                            ayuda={p.minimum !== undefined && p.maximum !== undefined ? `De ${String(p.minimum)} a ${String(p.maximum)}.` : null}
                            bloqueado={bloqueado}
                        />
                    );
                }
                return (
                    <Campo
                        key={clave}
                        etiqueta={nombre}
                        valor={typeof actual === 'string' ? actual : ''}
                        onChange={(v) => { cambiar(clave, v); }}
                        max={p.maxLength}
                        bloqueado={bloqueado}
                    />
                );
            })}
        </>
    );
}
